import { useEffect, useState } from "react";
import { Check, X, Loader2, FileDown, X as Close } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Read-only modal that shows the per-question answer log for a completed
 * assessment. Used from MyTests (Test Register), CompetencyCard, etc.
 *
 * Props:
 *   assessmentId: string — the assessment to load. When null/undefined the
 *                          modal stays closed.
 *   onClose: () => void
 */
export default function AnswerLogModal({ assessmentId, onClose }) {
  // Pack assessmentId together with payload so prop changes invalidate the
  // visible data synchronously (avoids set-state-in-effect lint issue).
  const [state, setState] = useState({ id: null, log: null, err: "" });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!assessmentId) return;
    let cancelled = false;
    api
      .get(`/assessments/${assessmentId}/answer-log`)
      .then((r) => {
        if (!cancelled) setState({ id: assessmentId, log: r.data, err: "" });
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            id: assessmentId,
            log: null,
            err: formatApiError(e.response?.data?.detail) || "Failed to load answer log",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  // Close on escape
  useEffect(() => {
    if (!assessmentId) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [assessmentId, onClose]);

  if (!assessmentId) return null;

  // Only show data when it matches the current prop — keeps the modal in a
  // clean loading state when assessmentId changes.
  const log = state.id === assessmentId ? state.log : null;
  const err = state.id === assessmentId ? state.err : "";

  const exportPdf = async () => {
    if (!log) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API}/assessments/${assessmentId}/answer-log/pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const safe = (log.employee?.name || "Employee").replace(/\s+/g, "_");
      const fallback = `AnswerLog_${safe}_${assessmentId}.pdf`;
      const filename = match ? match[1] : fallback;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Answer log PDF downloaded");
    } catch {
      toast.error("Export failed — please try again");
    } finally {
      setDownloading(false);
    }
  };

  const resultColor = log?.assessment?.result === "pass" ? "text-green-700" : "text-[#C41E1E]";
  const resultBg = log?.assessment?.result === "pass" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-6 px-2 md:px-6"
      data-testid="answer-log-modal"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5] bg-[#0D0D0D] text-white">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#C9A84C]">Assessment Answer Log</div>
            <div className="font-heading text-base font-semibold">{log?.employee?.name || "Loading…"}</div>
          </div>
          <div className="flex items-center gap-2">
            {log && (
              <button
                onClick={exportPdf}
                disabled={downloading}
                data-testid="answer-log-export-pdf"
                className="inline-flex items-center gap-1.5 bg-[#C41E1E] hover:bg-[#A31818] text-white text-xs rounded-sm px-3 py-1.5 disabled:opacity-50"
              >
                {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                {downloading ? "Generating…" : "Export PDF"}
              </button>
            )}
            <button
              onClick={onClose}
              data-testid="answer-log-close"
              className="p-1.5 rounded-sm hover:bg-white/10"
              aria-label="Close"
            >
              <Close size={18} />
            </button>
          </div>
        </div>

        {err && <div className="p-6 text-sm text-[#C41E1E]" data-testid="answer-log-error">{err}</div>}
        {!log && !err && (
          <div className="p-10 text-sm text-[#666666] flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading answer log…
          </div>
        )}

        {log && (
          <div className="p-5 space-y-5" data-testid="answer-log-content">
            {/* Header summary card */}
            <div className={`border rounded-sm p-4 ${resultBg}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
                <div>
                  <div className="bc-label">Skill</div>
                  <div className="font-medium">{log.assessment.skill_name}</div>
                </div>
                <div>
                  <div className="bc-label">Type</div>
                  <div className="font-medium">{log.assessment.type}</div>
                </div>
                <div>
                  <div className="bc-label">Date taken</div>
                  <div className="font-mono text-xs">{log.assessment.completed_date || "—"}</div>
                </div>
                <div>
                  <div className="bc-label">Score</div>
                  <div className="font-mono">{log.assessment.score_percent}%</div>
                </div>
                <div>
                  <div className="bc-label">Department</div>
                  <div>{log.employee.department || "—"}</div>
                </div>
                <div>
                  <div className="bc-label">Required</div>
                  <div className="font-mono">{log.assessment.required_level}</div>
                </div>
                <div>
                  <div className="bc-label">Achieved</div>
                  <div className="font-mono">{log.assessment.achieved_level || "—"}</div>
                </div>
                <div>
                  <div className="bc-label">Result</div>
                  <div className={`font-semibold uppercase ${resultColor}`} data-testid="answer-log-result">
                    {log.assessment.result}
                  </div>
                </div>
              </div>
            </div>

            {/* Question list */}
            {log.questions.length === 0 && (
              <div className="text-sm text-[#666666] italic">No answers recorded for this assessment.</div>
            )}
            {log.questions.map((q) => (
              <QuestionRow key={`${q.question_number}-${q.question_id}`} q={q} />
            ))}

            {/* Footer summary */}
            {log.summary.total > 0 ? (
              <div
                className="border-t border-[#E5E5E5] pt-4 flex items-center justify-between"
                data-testid="answer-log-summary"
              >
                <div className="text-sm">
                  <span className="bc-label mr-2">Score</span>
                  <span className="font-mono text-base font-semibold">
                    {log.summary.correct_count} out of {log.summary.total} correct
                  </span>
                </div>
                <div className="text-xs text-[#666666]">Assessment ID: {log.assessment.assessment_id}</div>
              </div>
            ) : (
              <div
                className="border-t border-[#E5E5E5] pt-4 flex items-center justify-end"
                data-testid="answer-log-summary"
              >
                <div className="text-xs text-[#666666]">Assessment ID: {log.assessment.assessment_id}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionRow({ q }) {
  const sel = (q.selected_option || "").toUpperCase();
  const cor = (q.correct_option || "").toUpperCase();
  return (
    <div
      className="border border-[#E5E5E5] rounded-sm overflow-hidden"
      data-testid={`answer-log-question-${q.question_number}`}
    >
      <div className="px-4 py-2.5 bg-[#F9F9F9] border-b border-[#E5E5E5] flex items-start justify-between gap-3">
        <div className="text-sm">
          <span className="font-semibold mr-2">Q{q.question_number}.</span>
          {q.question_text}
        </div>
        <div className="shrink-0">
          {q.is_correct ? (
            <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium" data-testid={`q${q.question_number}-correct`}>
              <Check size={14} /> Correct
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[#C41E1E] text-xs font-medium" data-testid={`q${q.question_number}-wrong`}>
              <X size={14} /> Wrong
            </span>
          )}
        </div>
      </div>
      <ul className="divide-y divide-[#E5E5E5]">
        {["A", "B", "C", "D"].map((letter) => {
          const isSel = letter === sel;
          const isCor = letter === cor;
          let bg = "";
          if (isSel && isCor) bg = "bg-green-200";
          else if (isSel) bg = "bg-blue-100";
          else if (isCor) bg = "bg-green-100";
          return (
            <li
              key={letter}
              className={`px-4 py-2 text-sm flex items-start gap-3 ${bg}`}
              data-testid={`q${q.question_number}-opt-${letter}`}
            >
              <span className="font-mono font-semibold w-5">{letter}.</span>
              <span className="flex-1">{q.options?.[letter] || "—"}</span>
              <span className="text-xs">
                {isSel && isCor && (
                  <span className="text-green-700 font-medium">Employee&apos;s answer · Correct</span>
                )}
                {isSel && !isCor && (
                  <span className="text-blue-700 font-medium">Employee&apos;s answer</span>
                )}
                {!isSel && isCor && (
                  <span className="text-green-700 font-medium">Correct answer</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
